import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { paragraphsOrPrompt } from "@/lib/tool-kit/essay-templates/text";

/**
 * A reflective piece built stage by stage from a published reflection model.
 *
 * Kolb's four-stage experiential learning cycle and Driscoll's three-question model share
 * one shape: the writer's own words for each stage, in order, with the stage's guiding
 * question (and any trigger questions) in place of a stage not yet written, and a
 * reference list for the model at the end — a reflection that uses a model is expected to
 * cite it. The stages, questions and references are data supplied by the tool, so the same
 * builder serves both models and every translation.
 */

/** A reference as runs, so the title can be italic: before + title (italic) + after. */
export interface ReferenceParts {
  before: string;
  title: string;
  after: string;
}

export interface StagedLabels<S extends string> {
  defaultTitle: string;
  titleWithTopic: string;
  subtitle: string;
  voiceNote: string;
  stageHeading: string;
  stagePrompt: string;
  stagePromptNamed: string;
  stageNames: Record<S, string>;
  stageQuestions: Record<S, string>;
  stageGuides: Record<S, string>;
  /** Optional trigger questions for each stage; shown while the stage is empty. */
  stageTriggers?: Record<S, readonly string[]>;
  triggersLead?: string;
  referenceHeading: string;
  referenceNote: string;
  references: readonly ReferenceParts[];
}

export interface StagedInput<S extends string> {
  topic: string;
  /** One heading per stage, or continuous prose for assignments that want an essay. */
  headings: boolean;
  stages: Record<S, string>;
}

function triggerBlock<S extends string>(
  stage: S,
  labels: StagedLabels<S>
): DocBlock[] {
  const triggers = labels.stageTriggers?.[stage] ?? [];
  if (triggers.length === 0) {
    return [];
  }
  const lead = labels.triggersLead ? `${labels.triggersLead} ` : "";
  return [{ kind: "guidance", text: `${lead}${triggers.join(" ")}` }];
}

export function buildStagedReflection<S extends string>(
  order: readonly S[],
  input: StagedInput<S>,
  labels: StagedLabels<S>
): DocumentModel {
  const topic = input.topic.trim();
  const stages: DocSection[] = order.map((stage, index) => {
    const values = {
      stage: labels.stageNames[stage],
      question: labels.stageQuestions[stage],
      guide: labels.stageGuides[stage],
    };
    const prompt = t(
      input.headings ? labels.stagePrompt : labels.stagePromptNamed,
      values
    );
    const written = paragraphsOrPrompt(input.stages[stage], prompt);
    const empty = written.length === 1 && written[0].kind === "prompt";
    return {
      heading: input.headings
        ? t(labels.stageHeading, { n: index + 1, stage: values.stage })
        : undefined,
      blocks: empty ? [...written, ...triggerBlock(stage, labels)] : written,
    };
  });
  return {
    title: topic ? t(labels.titleWithTopic, { topic }) : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      { blocks: [{ kind: "guidance", text: labels.voiceNote }] },
      ...stages,
      {
        heading: labels.referenceHeading,
        blocks: [
          ...labels.references.map(
            (reference): DocBlock => ({
              kind: "paragraph",
              text: [
                { text: reference.before },
                { text: reference.title, italic: true },
                { text: reference.after },
              ],
            })
          ),
          { kind: "guidance", text: labels.referenceNote },
        ],
      },
    ],
  };
}
